import {
  aquaAxiosWithCreds,
  callAsyncWithRetry,
  dataConverter,
  getBookGameId,
} from 'root/lib/utilityFunctions';
import Env from 'root/lib/Env';
import { getTimestamp } from 'root/lib/DatetimeManager';
import { getErrorActionObject } from '../components/GlobalErrorHandler/actions';
import { SEASON } from '../hub/data/constants';
import gameIds from '../hub/data/GameIds';
import { saveGameState } from '../actions/persistStateActions';
import createActionObj from '../actions/createActionObj';
import saveQuestSession from '../api/questSession';
import { saveApiFailures } from './apiFailuresActions';

const OBSERVATION_API_URL = `${Env.API_URL}/profiles/v${Env.API_VERSION_KEYCLOAK}/observation`;

const INVALID_QUEST_SESSION_ID_RESPONSE = 'Invalid questSessionId';

const sendCurrentMapQuestSessionId = () => (dispatch, getState) => {
  const { quests, currentActiveQuestId, contentServiceQuestId } = getState().questWorkflowReducer;
  const currentQuestData = quests[`quest${currentActiveQuestId}`];
  const { questSessionId } = currentQuestData;
  // save quests to server
  dispatch(saveGameState(
    gameIds.METAGAME_QUEST_CURRENT,
    dataConverter({
      currentQuest: {
        [`quest${currentActiveQuestId}`]: {
          ...currentQuestData,
          questSessionId,
          updated: getTimestamp(),
        },
      },
      contentServiceQuestId,
    }),
  ));
  const contentIds = [];
  let source = 'hard-coded';
  currentQuestData.steps.forEach((game) => {
    contentIds.push(game.skillId);
    if (game.source === 'adaptivity') {
      source = 'adaptivity';
    }
  });
  return saveQuestSession(currentActiveQuestId, questSessionId, `{${contentIds.join()}}`, source);
};

const sendCurrentWSQuestSessionId = () => (dispatch, getState) => {
  const { currentQuestId, workshop } = getState().workshopReducer;
  const { questSessionId, games } = workshop[`quest${currentQuestId}`];
  const contentIds = [];
  let source = 'hard-coded';
  games.forEach((game) => {
    contentIds.push(game.skillId);
    if (game.source === 'adaptivity') {
      source = 'adaptivity';
    }
  });
  if (currentQuestId) {
    dispatch(saveGameState(gameIds.METAGAME_WORKSHOP_CURRENT,
      {
        currentQuest: {
          [`quest${currentQuestId}`]: {
            ...workshop[`quest${currentQuestId}`],
            updated: getTimestamp(),
          },
        },
      },
    ));
  }
  return saveQuestSession(currentQuestId, questSessionId, `{${contentIds.join()}}`, source);
};

const recordObservationFailure = (gameLevel, isResolved, observationData = {}) => createActionObj(
  gameIds.METAGAME,
  'SET_OBSERVATION_API_FAILURE_DATA',
  { gameLevel, isResolved, observationData },
);

/**
 * Updates questWorkflowReducer to save observationApiFailures or remove it after a successful
 * resend, then persists the updated API_FAILURES gamestate bucket.
 * @param {string} gameLevel - GameId_Level
 * @param {boolean} isResolved - true removes the stored entry, false keeps it queued to retry
 * @param {object} observationData - postConfig from original observation API
 */
export const setObservationApiFailureData = (gameLevel, isResolved, observationData = {}) => (dispatch) => {
  dispatch(recordObservationFailure(gameLevel, isResolved, observationData));
  return dispatch(saveApiFailures()).catch((error) => console.error('Failed to save API_FAILURES bucket:', error));
};

const logObservationErrors = (gameId, error, { timesAttempted, isDoneRetrying }) => (dispatch) => {
  const category = 'API: observation';
  const data = `${category} | attempt ${timesAttempted}${isDoneRetrying ? ' (final)' : ''}`;
  const metricId = 'api';
  if (!isDoneRetrying) {
    dispatch(
      getErrorActionObject(
        gameId,
        error.toString(),
        category,
        data,
        metricId,
      ),
    );
  } else {
    console.error('Failed to send observation API after 3 attempts. Data will be stored in API_FAILURES bucket');
  }
};

export const postToObservationApi = roundData => async (dispatch, getState) => {
  const { persistStateReducer: { season, skillMap: SkillMap } } = getState();
  const {
    userData: {
      userId: UserId,
    },
    gameData: {
      details: {
        bookId,
        isSuccess: IsSuccess,
        isQuestWorkflow: IsQuest,
        level: Level,
      },
    },
    sessionData: {
      questSessionId,
    },
  } = roundData;
  let { gameData: { gameId: GameId } } = roundData;

  if (GameId === gameIds.E_READER) {
    GameId = getBookGameId(bookId);
  }

  const postConfig = {
    method: 'POST',
    data: {
      Meta: {
        UserId,
        ObservationType: 'level_result',
        questSessionId: IsQuest ? questSessionId || undefined : undefined,
        SkillMap,
      },
      Data: {
        GameId,
        Level,
        IsSuccess,
        IsQuest,
      },
    },
  };

  const asyncRetryFn = async () => {
    try {
      return await aquaAxiosWithCreds(OBSERVATION_API_URL, postConfig);
    } catch (error) {
      const { response } = error;
      // if error is an invalid questSessionId, use the right action depending on season to send current questSessionId
      // then retry this call to the observation API. Otherwise, throw error down the chain as it was.
      if (response?.data?.toString() === INVALID_QUEST_SESSION_ID_RESPONSE) {
        const idAction = season === SEASON.GRADE_4_5 ? sendCurrentWSQuestSessionId : sendCurrentMapQuestSessionId;
        await dispatch(idAction());
        return asyncRetryFn();
      }
      throw error;
    }
  };

  const asyncRetryConfig = {
    logger: async (error, { timesAttempted, isDoneRetrying }) => {
      dispatch(logObservationErrors(GameId, error, { timesAttempted, isDoneRetrying }));
      // After all retries have failed, store observation API failure data in API_FAILURES gamestate bucket
      if (isDoneRetrying) {
        dispatch(setObservationApiFailureData(`${GameId}_${Level}`, false, postConfig));
      }
    },
  };

  return callAsyncWithRetry(asyncRetryFn, asyncRetryConfig);
};

/**
 * Retries /observation calls for each failed observation stored in the API_FAILURES gamestate bucket.
 * Runs via getAllGamesState, which fires on login, refresh, and other times throughout the app.
 * Clears an entry on a successful resend and leaves it queued to retry on any failure.
 * @param {object} failedObservationData - stored failed /observation calls, eg { gameId_level: postConfig }
 */
export const resendFailedObservations = failedObservationData => async (dispatch) => {
  await Promise.all(Object.values(failedObservationData).map(async (postConfig) => {
    if (!postConfig?.data) {
      return;
    }
    try {
      const { GameId, Level } = postConfig.data.Data; // same postConfig from postToObservationApi
      await aquaAxiosWithCreds(OBSERVATION_API_URL, postConfig);
      dispatch(recordObservationFailure(`${GameId}_${Level}`, true)); // remove failure data from gamestate
    } catch (error) {
      console.error('Failed to resend observation API:', error);
    }
  }));

  return dispatch(saveApiFailures()).catch((error) => console.error('Failed to save API_FAILURES bucket:', error));
};

export default {};
